import React from 'react';
import { K, PRECONNECTS, inputStyle } from '../constants';
import { clamp } from '../utils/formulas';
import { Icon } from './Icon';

export function AuthGate({ user, onShowAuth, label = 'Members only', message = 'This content is available to Central Mason Fire & EMS members. Log in to access.' }) {
  if (user) return null;
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: `${K.red}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <Icon name="lock" size={26} color={K.red} strokeWidth={1.75} />
      </div>
      <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 600, color: K.t0, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: K.fSans, fontSize: 13, color: K.t2, marginBottom: 28, lineHeight: 1.6, maxWidth: 360, marginInline: 'auto' }}>{message}</div>
      <Btn onClick={onShowAuth} col={K.red}>Log in to access</Btn>
    </div>
  );
}

export function NI({ v, set, min, max, step = 1, dec = false }) {
  return (
    <input
      type="number"
      value={v}
      min={min}
      max={max}
      step={step}
      inputMode={dec ? 'decimal' : 'numeric'}
      onChange={(e) => set(dec ? parseFloat(e.target.value) || 0 : parseInt(e.target.value || '0', 10) || 0)}
      style={inputStyle()}
    />
  );
}

export function Sel({ v, set, children }) {
  return (
    <select value={v} onChange={(e) => set(e.target.value)} style={{ ...inputStyle(), cursor: 'pointer' }}>
      {children}
    </select>
  );
}

export function Lbl({ t }) {
  return (
    <div style={{ fontFamily: K.fSans, fontSize: 12, color: K.t3, letterSpacing: 0.4, marginBottom: 6, fontWeight: 600 }}>{t}</div>
  );
}

export function SecHd({ title, sub }) {
  return (
    <div className="hc-sec-hd" style={{ marginBottom: 18, paddingBottom: 12, borderBottom: `1px solid ${K.bdr}` }}>
      <h2 style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 600, margin: 0, color: K.t0, lineHeight: 1.15 }}>{title}</h2>
      {sub ? <p style={{ margin: '4px 0 0', fontFamily: K.fSans, fontSize: 12.5, color: K.t2, lineHeight: 1.5 }}>{sub}</p> : null}
    </div>
  );
}

export function Card({ children, accent = K.bdr, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderLeft: `3px solid ${accent}`, borderRadius: 12, padding: 16, boxShadow: K.sh1, ...style }}>
      {children}
    </div>
  );
}

export function Metric({ label, value, sub, col = K.gold, sm = false }) {
  return (
    <div style={{ background: K.bg2, border: `1px solid ${K.bdr}`, borderTop: `2px solid ${col}`, borderRadius: 10, padding: '14px 12px', textAlign: 'center' }}>
      <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2, letterSpacing: 0.4, marginBottom: 8, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: K.fMono, fontSize: sm ? 24 : 32, fontWeight: 500, lineHeight: 1, color: col }}>{value}</div>
      {sub ? <div style={{ fontFamily: K.fSans, fontSize: 12, color: K.t2, marginTop: 6 }}>{sub}</div> : null}
    </div>
  );
}

export function Btn({ children, onClick, col = K.red, outline = false, block = false, disabled = false, ariaLabel, style = {} }) {
  return (
    <button
      className="hc-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        background: outline ? 'transparent' : disabled ? K.bg3 : col,
        color: outline ? col : disabled ? K.t3 : '#fff',
        border: `1.5px solid ${disabled ? K.bdr : col}`,
        borderRadius: 9,
        padding: '11px 18px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: K.fSans,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.2,
        width: block ? '100%' : 'auto',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function AddBtn({ children, onClick, col = K.red }) {
  return (
    <button
      className="hc-btn"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: `1.5px dashed ${col}66`,
        color: col,
        borderRadius: 9,
        padding: '11px 16px',
        width: '100%',
        cursor: 'pointer',
        fontFamily: K.fSans,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.2,
      }}
    >
      {children}
    </button>
  );
}

export function MathBox({ show, steps }) {
  if (!show || !steps?.length) return null;
  return (
    <div style={{ background: K.bg, border: `1px solid ${K.bdr2}`, borderLeft: `3px solid ${K.gold}`, borderRadius: 10, padding: '14px 16px', marginTop: 12, fontSize: 11.5, color: K.t2, lineHeight: 1.9, fontFamily: K.fMono }}>
      <div style={{ fontFamily: K.fSans, color: K.gold, fontSize: 11, fontWeight: 600, letterSpacing: 0.6, marginBottom: 10 }}>Breakdown</div>
      {steps.map((s, i) => (
        <div key={i} style={{ borderBottom: i < steps.length - 1 ? `1px solid ${K.bdr}` : 'none', paddingBottom: 4, marginBottom: 4 }}>
          <span style={{ color: K.t3, marginRight: 6 }}>{String(i + 1).padStart(2, '0')}.</span>
          <span style={{ color: K.t1 }}>{s.l}: </span>
          <span style={{ color: K.gold }}>{s.f}</span>
          {s.r ? <span style={{ color: K.ok }}> = {s.r}</span> : null}
        </div>
      ))}
    </div>
  );
}

export function TimerBar({ timer }) {
  const mins = timer.elapsed / 60000;
  const col = mins > 20 ? K.bad : mins > 10 ? K.warn : K.ok;
  const pct = clamp((mins / 30) * 100, 0, 100);
  const idle = !timer.running && timer.elapsed === 0;

  return (
    <div style={{ background: K.bg1, borderBottom: `1px solid ${K.bdr}`, position: 'relative', overflow: 'hidden' }}>
      {!idle ? <div style={{ position: 'absolute', bottom: 0, left: 0, height: 3, width: `${pct}%`, background: col, transition: 'width .5s linear' }} /> : null}
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: idle ? K.t3 : col, boxShadow: timer.running ? `0 0 10px ${col}88` : 'none' }} />
        <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          <span style={{ fontFamily: K.fMono, fontSize: 34, fontWeight: 500, letterSpacing: 1, color: idle ? K.t3 : col, lineHeight: 1 }}>{timer.fmt}</span>
          {!idle ? <span style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2 }}>{mins.toFixed(1)} min</span> : null}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {!timer.running ? (
            <button onClick={timer.start} style={{ background: K.ok, border: 'none', color: '#fff', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontFamily: K.fSans }}>{timer.elapsed > 0 ? '▶ Resume' : '▶ Start'}</button>
          ) : (
            <button onClick={timer.pause} style={{ background: K.warn, border: 'none', color: '#fff', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontFamily: K.fSans }}>⏸ Pause</button>
          )}
          {timer.elapsed > 0 ? <button onClick={timer.reset} style={{ background: K.bg3, border: `1px solid ${K.bdr}`, color: K.t1, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontFamily: K.fSans }}>Reset</button> : null}
        </div>
      </div>
    </div>
  );
}

export function Banner({ preconnects = PRECONNECTS }) {
  return (
    <div style={{ background: K.bg1, borderBottom: `1px solid ${K.bdr}`, padding: '8px 10px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontFamily: K.fSans, fontSize: 10.5, color: K.t2, letterSpacing: 0.4, fontWeight: 600 }}>Fast PDP</span>
      {preconnects.map((p) => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 8, padding: '4px 8px' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.col, display: 'inline-block' }} />
          <span style={{ fontFamily: K.fSans, fontSize: 11, color: K.t1, fontWeight: 500 }}>{p.name}</span>
          <span style={{ fontFamily: K.fMono, fontSize: 15, fontWeight: 500, color: p.col }}>{p.pdp}</span>
        </div>
      ))}
    </div>
  );
}

export function ModeSwitchBtn({ label, onClick }) {
  return (
    <button className="hc-btn" onClick={onClick} style={{ background: K.bg2, border: `1px solid ${K.bdr}`, color: K.t2, padding: '8px 14px', borderRadius: 9, cursor: 'pointer', fontSize: 12.5, fontFamily: K.fSans, fontWeight: 500, whiteSpace: 'nowrap' }}>{label} →</button>
  );
}

export function ModeToggle({ mode, setMode }) {
  const modes = [
    { id: 'ops',      label: 'Pump Ops', col: K.red  },
    { id: 'command',  label: 'Command',  col: K.info },
    { id: 'training', label: 'Training', col: K.gold },
    { id: 'ems',      label: 'EMS',      col: K.ems  },
  ];
  return (
    <div style={{ marginBottom: 20, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{ display: 'inline-flex', background: K.bg2, borderRadius: 11, border: `1px solid ${K.bdr}`, padding: 3, gap: 3, minWidth: '100%', justifyContent: 'center' }}>
        {modes.map((m) => {
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              style={{
                padding: '9px 14px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontFamily: K.fSans,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: 0.2,
                transition: 'all .2s',
                background: active ? m.col : 'transparent',
                color: active ? '#fff' : K.t3,
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function IncidentBar({ incident, elapsedFmt, onView }) {
  if (!incident || incident.status !== 'active') return null;
  const type = incident.incidentType || 'other';
  return (
    <div
      onClick={onView}
      style={{ background: K.bg1, borderBottom: `2px solid ${K.red}`, padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: K.red, boxShadow: `0 0 10px ${K.red}88`, animation: 'pulse 1.5s infinite' }} />
        <div>
          <div style={{ fontFamily: K.fSans, fontSize: 12, color: K.t0, fontWeight: 600 }}>{incident.address || 'Active incident'}</div>
          <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2, textTransform: 'capitalize' }}>{type}</div>
        </div>
      </div>
      <div style={{ fontFamily: K.fMono, fontSize: 22, fontWeight: 500, color: K.red, letterSpacing: 1 }}>{elapsedFmt}</div>
    </div>
  );
}

export function HomeTile({ icon, iconName, title, sub, col = K.red, onClick }) {
  return (
    <button className="hc-tile" onClick={onClick} style={{ background: K.surf, border: `1px solid ${K.bdr}`, borderRadius: 12, padding: 14, textAlign: 'left', cursor: 'pointer', boxShadow: K.sh2 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: `${col}26`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
        {iconName ? <Icon name={iconName} size={20} color={col} strokeWidth={1.75} /> : icon ? <span style={{ fontSize: 20 }}>{icon}</span> : null}
      </div>
      <div style={{ fontFamily: K.fSans, fontSize: 15, fontWeight: 600, color: K.t0, lineHeight: 1.2, marginBottom: 3 }}>{title}</div>
      <div style={{ fontFamily: K.fSans, fontSize: 11.5, color: K.t3, lineHeight: 1.45 }}>{sub}</div>
    </button>
  );
}
